// Pro's Library files: the Library's own content (`features/library`, `LibraryView`) inside Pro's
// shell, so a project, a file's preview, its moves, renames, deletes and uploads all work in Pro
// (spec ui-perf/ui-switch-classic-simple-pro, PR e3). Composed here: a feature never imports
// another; the composition root may.
import { createRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

import { librarySearch, type LibrarySearch } from "../features/library/library-search";
import { LibraryView } from "../features/library/LibraryScreen";
import { studio2Route } from "../features/studio2/routes";
import { ProjectLinkProvider } from "../platform/media/project-link";
import { useProProjectLink } from "./pro-project-link";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/pro/library/files";

/** `pro/library/files`, with the Library's own view in its URL (`?folder=`, `?preview=`...). */
export const studio2LibraryFilesRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "library/files",
  validateSearch: (search) => librarySearch.parse(search),
  component: ProLibraryFiles,
});

function ProLibraryFiles() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/pro/library/files" });
  const onSearch = useCallback(
    (update: (current: LibrarySearch) => LibrarySearch) => navigate({ search: update, replace: true }),
    [navigate],
  );
  // A file menu's project opens here, in Pro, as the Library page's opens there.
  const projectLink = useProProjectLink(teamId, brandId);
  // Another brand starts the Library anew, as the Library page's own route does.
  return (
    <ProjectLinkProvider link={projectLink}>
      <LibraryView
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        brandId={brandId}
        search={search}
        onSearch={onSearch}
      />
    </ProjectLinkProvider>
  );
}
